import { Container, Title, Text, Button, Group, Stack, Paper } from '@mantine/core';
import { IconArrowLeft, IconHome, IconFileText, IconBrandGithub } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/hooks';

interface NotFoundPageProps {
  onMobileMenuStateChange?: (state: {
    pdfFile?: File | null;
    isTopSectionExpanded: boolean;
    onToggleTopSection: () => void;
  }) => void;
}

export function NotFoundPage({ onMobileMenuStateChange: _ }: NotFoundPageProps) {
  // Explicitly acknowledge the unused parameter to satisfy ESLint
  void _;
  const navigate = useNavigate();
  const { translations } = useLanguage();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Container size="md" style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper p="xl" shadow="sm" style={{ textAlign: 'center', maxWidth: 500 }}>
        <Stack gap="lg">
          <div>
            <Title order={1} size="h1" style={{ fontSize: '6rem', fontWeight: 900, color: '#1971c2' }}>
              404
            </Title>
            <Title order={2} size="h2" mb="sm">
              {translations.notFound.title || 'Page Not Found'}
            </Title>
            <Text size="lg" c="dimmed">
              {translations.notFound.description || 
                'The page you are looking for does not exist. It might have been moved, deleted, or you entered the wrong URL.'}
            </Text>
          </div>

          <Group justify="center" gap="md">
            <Button
              variant="filled"
              leftSection={<IconHome size={16} />}
              onClick={handleGoHome}
              size="md"
            >
              {translations.notFound.goHome || 'Go Home'}
            </Button>
            <Button
              variant="outline"
              leftSection={<IconArrowLeft size={16} />}
              onClick={handleGoBack}
              size="md"
            >
              {translations.notFound.goBack || 'Go Back'}
            </Button>
          </Group>

          <Stack gap="sm" mt="xl">
            <Text size="sm" c="dimmed" fw={500}>
              {translations.notFound.helpfulLinks || 'Helpful Links:'}
            </Text>
            <Group justify="center" gap="md">
              <Button
                variant="subtle"
                leftSection={<IconFileText size={16} />}
                onClick={() => navigate('/pdf-editor')}
                size="sm"
              >
                {translations.notFound.pdfEditor || 'PDF Editor'}
              </Button>
              <Button
                variant="subtle"
                leftSection={<IconBrandGithub size={16} />}
                component="a"
                href="https://github.com/guigui42/form-pdf.com"
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
              >
                {translations.notFound.github || 'GitHub'}
              </Button>
            </Group>
          </Stack>

          <Text size="xs" c="dimmed" mt="md">
            {translations.notFound.contact || 'If you think this is a mistake, please contact us.'}
          </Text>
        </Stack>
      </Paper>
    </Container>
  );
}